import type { IncidentSortField, IncidentStatus, SortDirection } from "./api";
import { STATUS_ORDER } from "./status";

/**
 * Validated /incidents search params (TanStack Router validateSearch).
 *
 * The URL is where the list's view lives ([F-011]; the build feasibility
 * check's reading of AC-012's "Seitenwechsel"): every filter, the search term
 * and the sort. So the browser's back button, a reload and a shared link all
 * show the same list, and the dashboard drilldowns (F-020) are simply links
 * into it.
 */
export interface IncidentListSearch {
  /** Free-text search (F-011, CD-015). */
  q?: string;
  /** Single-status filter (the existing status Select). */
  status?: IncidentStatus;
  /** Comma-joined multi-status filter (dashboard "Offene Vorfälle" tiles). */
  statuses?: string;
  severityId?: string;
  categoryId?: string;
  /** ISO timestamp lower bound on createdAt. */
  createdAfter?: string;
  /** Resolved incidents without a postmortem (dashboard tile 2). */
  missingPostmortem?: boolean;
  /** Incidents with overdue follow-up tasks. */
  overdueFollowup?: boolean;
  /** F-011 Bereich, Tag and Owner filters. */
  areaId?: string;
  tagId?: string;
  ownerId?: string;
  /** F-011 Zeitraum on the Entdeckungszeitpunkt, as calendar days YYYY-MM-DD. */
  from?: string;
  to?: string;
  /** The "Meine" quick filter. */
  mine?: boolean;
  // GR-004: archived incidents join the list only when asked for.
  archived?: "include";
  /** F-011 sort; absent is the default, newest first. */
  sort?: IncidentSortField;
  dir?: SortDirection;
}

const SORT_FIELDS: readonly string[] = [
  "createdAt",
  "number",
  "title",
  "severity",
  "status",
  "discoveredAt",
] satisfies readonly IncidentSortField[];

function asSortField(value: unknown): IncidentSortField | undefined {
  return typeof value === "string" && SORT_FIELDS.includes(value)
    ? (value as IncidentSortField)
    : undefined;
}

function asDir(value: unknown): SortDirection | undefined {
  return value === "asc" || value === "desc" ? value : undefined;
}

function asDay(value: unknown): string | undefined {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : undefined;
}

function isIncidentStatus(value: unknown): value is IncidentStatus {
  return (
    typeof value === "string" &&
    (STATUS_ORDER as readonly string[]).includes(value)
  );
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function asFlag(value: unknown): true | undefined {
  return value === true || value === "true" ? true : undefined;
}

/** Keep only valid status tokens of a comma-joined list (order preserved). */
function asStatuses(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const valid = value.split(",").map((s) => s.trim()).filter(isIncidentStatus);
  return valid.length > 0 ? valid.join(",") : undefined;
}

function asIsoDate(value: unknown): string | undefined {
  const str = asString(value);
  if (!str || Number.isNaN(Date.parse(str))) return undefined;
  return str;
}

/**
 * Drop anything unknown or malformed instead of erroring: a bad deep link
 * degrades to the unfiltered list. `undefined` fields are omitted so the
 * object round-trips cleanly through the URL serializer.
 */
export function parseIncidentListSearch(
  search: Record<string, unknown>,
): IncidentListSearch {
  const status = isIncidentStatus(search.status) ? search.status : undefined;
  const statuses = asStatuses(search.statuses);
  const severityId = asString(search.severityId);
  const categoryId = asString(search.categoryId);
  const createdAfter = asIsoDate(search.createdAfter);
  const missingPostmortem = asFlag(search.missingPostmortem);
  const overdueFollowup = asFlag(search.overdueFollowup);
  // A number-looking search term arrives from the router as a number.
  const q =
    typeof search.q === "number" ? String(search.q) : asString(search.q)?.trim() || undefined;
  const areaId = asString(search.areaId);
  const tagId = asString(search.tagId);
  const ownerId = asString(search.ownerId);
  const from = asDay(search.from);
  const to = asDay(search.to);
  const mine = asFlag(search.mine);
  const archived = search.archived === "include" ? ("include" as const) : undefined;
  const sort = asSortField(search.sort);
  const dir = asDir(search.dir);
  return {
    ...(q ? { q } : {}),
    ...(status ? { status } : {}),
    ...(statuses ? { statuses } : {}),
    ...(severityId ? { severityId } : {}),
    ...(categoryId ? { categoryId } : {}),
    ...(createdAfter ? { createdAfter } : {}),
    ...(missingPostmortem ? { missingPostmortem } : {}),
    ...(overdueFollowup ? { overdueFollowup } : {}),
    ...(areaId ? { areaId } : {}),
    ...(tagId ? { tagId } : {}),
    ...(ownerId ? { ownerId } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    ...(mine ? { mine } : {}),
    ...(archived ? { archived } : {}),
    ...(sort ? { sort } : {}),
    ...(dir ? { dir } : {}),
  };
}
